import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { decrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import stockCategoryService from "../../services/stockCategoryService";
import stockProcessingService from "../../services/stockProcessingService";
import lotService from "../../services/lotService";
import yardService from "../../services/yardService";
import alertService from "../../utils/alertService";
import { formatDate } from "../../utils/dateUtils";
import MasterDetailSummary from "../../components/common/MasterDetailSummary";
import CrudTable from "../../components/common/crud/CrudTable";
import Modal from "../../components/common/Modal";
import { useMasterModals } from "../../hooks/useMasterModals";

const DEFAULT_ENTRY_COUNT = 2;

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function createEntry() {
  return {
    stockCategoryId: "",
    yardId: "",
    lotId: "",
    weight: "",
    isJoint: 0,
  };
}

const WEIGHT_INPUT_PATTERN = /^\d{0,9}(\.\d{0,2})?$/;

function BulkStockProcessingCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");
  const lotIdParam = searchParams.get("lotId");
  const dateParam = searchParams.get("date");
  const stockProcessingIdParam = searchParams.get("stockProcessingId");
  const viewParam = searchParams.get("view"); // "history"
  const originParam = searchParams.get("origin");
  const isLockedParam =
    searchParams.get("isLocked") === "true" ||
    searchParams.get("isLocked") === "1";

  const stockDetailIdFromUrl = useMemo(
    () => (encryptedId ? decrypt(encryptedId) : null),
    [encryptedId],
  );

  const isEditMode =
    Boolean((lotIdParam && dateParam) || stockProcessingIdParam) && !viewParam;

  const [isLocked, setIsLocked] = useState(isLockedParam);
  const [stockCategoryOptions, setStockCategoryOptions] = useState([]);
  const [lotOptions, setLotOptions] = useState([]);
  const [yardOptions, setYardOptions] = useState([]);
  const [
    activeBlContainerStockDetailsOptions,
    setActiveBlContainerStockDetailsOptions,
  ] = useState([]);
  const [selectedStockDetail, setSelectedStockDetail] = useState(
    location.state?.stockDetail || null,
  );
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // History Data
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [rowDetails, setRowDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [transferHistoryItems, setTransferHistoryItems] = useState([]);
  const [isLoadingTransferHistory, setIsLoadingTransferHistory] =
    useState(false);

  const [lotwiseStock, setLotwiseStock] = useState([]);
  const [isLoadingLotwise, setIsLoadingLotwise] = useState(false);
  const [originalProcessingWeight, setOriginalProcessingWeight] = useState(0);
  const [activeCategoryId, setActiveCategoryId] = useState("");

  const todayDate = useMemo(() => getTodayDateString(), []);
  const [form, setForm] = useState({
    blContainerStockDetailsId: stockDetailIdFromUrl || "",
    lotId: lotIdParam || location.state?.stockDetail?.lotId || "",
    date: dateParam || getTodayDateString(),
    stockCategoryId: "",
    labourRate: "",
    additionEntries: Array.from({ length: DEFAULT_ENTRY_COUNT }, () =>
      createEntry(),
    ),
  });

  const isProcessingJointDisabled = useMemo(() => {
    if (!isEditMode) return false;

    // Check if the selected category is joint in lotwiseStock
    const selectedStock = lotwiseStock.find(
      (s) => String(s.stockCategoryId) === String(form.stockCategoryId),
    );
    if (selectedStock && selectedStock.isJoint) return true;

    // Or check if any addition entry has isOriginalJoint
    return form.additionEntries.some((e) => !!e.isOriginalJoint);
  }, [isEditMode, form.stockCategoryId, lotwiseStock, form.additionEntries]);

  const minProcessingDate = useMemo(() => {
    if (!form.stockCategoryId || lotwiseStock.length === 0) return undefined;
    const selectedStock = lotwiseStock.find(
      (s) => String(s.stockCategoryId) === String(form.stockCategoryId),
    );
    if (selectedStock && selectedStock.convertedStockDate) {
      return selectedStock.convertedStockDate.split("T")[0];
    }
    return undefined;
  }, [form.stockCategoryId, lotwiseStock]);

  // Sync isLocked if we have stockDetail from state or query params
  useEffect(() => {
    const isLockedFromUrl =
      searchParams.get("isLocked") === "true" ||
      searchParams.get("isLocked") === "1";
    const isLockedFromState =
      location.state?.stockDetail?.isLocked === true ||
      location.state?.stockDetail?.isLocked === 1;
    const isLockedFromDetail =
      selectedStockDetail?.isLocked === true ||
      selectedStockDetail?.isLocked === 1;

    if (isLockedFromUrl || isLockedFromState || isLockedFromDetail) {
      setIsLocked(true);
    }
  }, [location.state, searchParams, selectedStockDetail]);

  // Fetch Lot Status specifically to ensure lock logic is respected lot-wise
  useEffect(() => {
    const lotId = lotIdParam || form.lotId;
    if (!lotId || isLocked) return;

    let isMounted = true;
    const checkLotLock = async () => {
      try {
        const lot = await lotService.getLotById(lotId);
        if (isMounted && (lot?.isLocked === true || lot?.isLocked === 1)) {
          setIsLocked(true);
        }
      } catch (err) {
        console.error("Failed to check lot status", err);
      }
    };
    checkLotLock();
    return () => {
      isMounted = false;
    };
  }, [lotIdParam, form.lotId, isLocked]);

  // Split Modal State
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitForm, setSplitForm] = useState({
    stockCategoryId: "",
    yardId: "",
    lotId: "",
    weight: "",
    isJoint: 1, // Default Joint = true
  });
  const [splitFieldErrors, setSplitFieldErrors] = useState({});

  const handleOpenSplitModal = () => {
    setSplitForm({
      stockCategoryId: "",
      yardId: "",
      lotId: "",
      weight: "",
      isJoint: 1,
    });
    setSplitFieldErrors({});
    setShowSplitModal(true);
  };

  const loadLots = useCallback(async () => {
    try {
      const response = await lotService.getActiveLots();
      setLotOptions(
        (response?.items || []).map((l) => ({
          label: l.lotNo,
          value: String(l.lotId),
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadStockCategories = useCallback(async () => {
    try {
      const response = await stockCategoryService.getActiveStockCategories();
      setStockCategoryOptions(
        (response?.items || []).map((c) => ({
          label: c.stockCategoryName,
          value: String(c.stockCategoryId),
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadYards = useCallback(async () => {
    try {
      const response = await yardService.getActiveYards();
      setYardOptions(
        (response?.items || response?.data || []).map((y) => ({
          label: y.yardNo || y.yardName,
          value: String(y.yardId),
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const { openModal, renderModals } = useMasterModals({
    stockCategory: loadStockCategories,
    yard: loadYards,
    lot: loadLots,
  });

  // --- Initial Load Options ---
  useEffect(() => {
    let isMounted = true;

    const loadContainers = async () => {
      if (stockDetailIdFromUrl || isEditMode) return;
      try {
        const response =
          await stockProcessingService.getActiveBLContainerStockDetails();
        if (isMounted) {
          setActiveBlContainerStockDetailsOptions(
            (response?.items || []).map((item) => ({
              label:
                [item?.blContainerName, item?.lotName, item?.subCategoryName]
                  .filter(Boolean)
                  .join("-") || `#${item.blContainerStockDetailsId}`,
              value: String(item.blContainerStockDetailsId),
            })),
          );
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadStockCategories();
    loadYards();
    loadLots();
    loadContainers();
    return () => {
      isMounted = false;
    };
  }, [
    stockDetailIdFromUrl,
    isEditMode,
    loadStockCategories,
    loadYards,
    loadLots,
  ]);

  const loadLotwiseData = useCallback(async () => {
    const lotId = lotIdParam || form.lotId;
    if (!lotId) return;
    setIsLoadingLotwise(true);
    try {
      const response = await stockProcessingService.getLotwiseStock({ lotId });
      const items = response.items || [];
      setLotwiseStock(items);

      // Pre-fill the first category from Available Stock if not already set and not in edit mode
      if (items.length > 0 && !isEditMode) {
        setForm((prev) => {
          const defaultCategory = String(items[0].stockCategoryId);
          const targetCategory = prev.stockCategoryId || defaultCategory;
          setActiveCategoryId(targetCategory);
          if (!prev.stockCategoryId) {
            return {
              ...prev,
              stockCategoryId: targetCategory,
            };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error("Failed to load lotwise stock", err);
    } finally {
      setIsLoadingLotwise(false);
    }
  }, [lotIdParam, form.lotId, isEditMode, setActiveCategoryId]);

  const loadHistoryData = useCallback(async () => {
    const lotId = lotIdParam || form.lotId;
    const stockCategoryId = activeCategoryId;
    if (!lotId || !stockCategoryId) {
      setHistoryItems([]);
      setExpandedRows(new Set());
      return;
    }
    setIsLoadingHistory(true);
    try {
      const history = await stockProcessingService.getStockHistory({
        lotId,
        stockCategoryId,
        page: 1,
        limit: 50,
      });
      const items = history.items || [];
      setHistoryItems(items);
      setExpandedRows(new Set());
    } catch (err) {
      console.error("History load failed", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [lotIdParam, form.lotId, activeCategoryId]);

  const loadTransferHistoryData = useCallback(async () => {
    const lotId = lotIdParam || form.lotId;
    if (!lotId) {
      setTransferHistoryItems([]);
      return;
    }
    setIsLoadingTransferHistory(true);
    try {
      const response = await stockProcessingService.getStockTransferHistory({
        search: "",
        fromLotId: lotId,
        page: 1,
        limit: 10,
      });
      setTransferHistoryItems(response.items || []);
    } catch (err) {
      console.error("Failed to load stock transfer history", err);
    } finally {
      setIsLoadingTransferHistory(false);
    }
  }, [lotIdParam, form.lotId]);

  // --- Load Lotwise Stock ---
  useEffect(() => {
    loadLotwiseData();
  }, [loadLotwiseData]);

  // --- Load History / Edit Data ---
  useEffect(() => {
    let isMounted = true;
    const loadAll = async () => {
      // 1. Load history if we have category
      await loadHistoryData();

      // Load transfer history
      await loadTransferHistoryData();

      // 2. Load edit detail if in edit mode
      if (isEditMode) {
        try {
          const detail = await stockProcessingService.getStockProcessingDetail({
            lotId: lotIdParam,
            date: dateParam,
            stockProcessingId: stockProcessingIdParam,
          });

          if (isMounted && detail) {
            const currentWeight = Array.isArray(detail.additionEntries)
              ? detail.additionEntries.reduce(
                  (sum, e) => sum + (parseFloat(e.weight) || 0),
                  0,
                )
              : 0;
            setOriginalProcessingWeight(currentWeight);
            if (detail.stockCategoryId) {
              setActiveCategoryId(String(detail.stockCategoryId));
            }

            setForm((prev) => ({
              ...prev,
              blContainerStockDetailsId:
                detail.blContainerStockDetailsId ||
                prev.blContainerStockDetailsId,
              lotId: String(detail.lotId || lotIdParam || prev.lotId),
              date: detail.date
                ? detail.date.split("T")[0]
                : dateParam || prev.date,
              stockCategoryId: String(
                detail.stockCategoryId || prev.stockCategoryId,
              ),
              labourRate: String(detail.labourRate || prev.labourRate),
              additionEntries:
                Array.isArray(detail.additionEntries) &&
                detail.additionEntries.length > 0
                  ? detail.additionEntries.map((e) => ({
                      stockCategoryId: String(e.stockCategoryId),
                      yardId: String(e.yardId),
                      lotId: String(e.lotId),
                      weight: String(e.weight),
                      isJoint: e.isJoint ? 1 : 0,
                      isOriginalJoint: !!e.isJoint,
                    }))
                  : prev.additionEntries,
            }));
          }
        } catch (err) {
          console.error("Detail load failed", err);
        }
      }
    };

    loadAll();
    return () => {
      isMounted = false;
    };
  }, [
    lotIdParam,
    dateParam,
    stockProcessingIdParam,
    isEditMode,
    loadHistoryData,
    loadTransferHistoryData,
  ]);

  // --- Load Metadata ---
  useEffect(() => {
    // Skip if we already have data from location.state
    if (selectedStockDetail || !form.blContainerStockDetailsId) return;

    let isMounted = true;
    const loadMeta = async () => {
      setIsLoadingDetail(true);
      try {
        const detail =
          await stockProcessingService.getBLContainerStockDetailById(
            form.blContainerStockDetailsId,
          );
        if (isMounted) {
          const lid = detail.lotId || detail.lot?.lotId;
          if (lid) {
            if (!form.lotId) {
              setForm((prev) => ({ ...prev, lotId: String(lid) }));
            }
            try {
              const procList = await stockProcessingService.getStockProcessingDetails({ lotId: lid });
              const matched = procList.items.find(
                (item) => String(item.blContainerStockDetailsId) === String(form.blContainerStockDetailsId)
              );
              if (matched) {
                setSelectedStockDetail({
                  ...detail,
                  ...matched,
                });
                return;
              }
            } catch (err) {
              console.error("Failed to fetch computed stock details", err);
            }
          }
          setSelectedStockDetail(detail);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setIsLoadingDetail(false);
      }
    };
    loadMeta();
    return () => {
      isMounted = false;
    };
  }, [form.blContainerStockDetailsId, selectedStockDetail]);

  const updateField = (name, value) => {
    if (name === "labourRate") {
      const val = String(value || "").replace(/[^0-9.]/g, "");
      if (/^\d{0,3}(\.\d?)?$/.test(val) || !val)
        setForm((prev) => ({ ...prev, labourRate: val }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectCategoryFromGrid = (categoryId) => {
    setActiveCategoryId(categoryId);

    if (isEditMode && String(categoryId) !== String(form.stockCategoryId)) {
      // Clear edit params from URL to exit edit mode
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("stockProcessingId");
      nextParams.delete("date");
      if (originParam === "history") {
        nextParams.set("view", "history");
      }
      setSearchParams(nextParams);

      setOriginalProcessingWeight(0);
      setForm((prev) => ({
        ...prev,
        stockCategoryId: categoryId,
        labourRate: "",
        additionEntries: Array.from({ length: DEFAULT_ENTRY_COUNT }, () =>
          createEntry(),
        ),
      }));
      setFieldErrors({});
    } else {
      updateField("stockCategoryId", categoryId);
    }
  };

  const updateEntry = (index, name, value) => {
    setForm((prev) => {
      const entries = [...prev.additionEntries];
      let finalValue = value;

      if (name === "weight") {
        const numVal = parseFloat(value);
        if (!isNaN(numVal) && numVal < 0) {
          finalValue = "0";
        }
      }

      entries[index] = { ...entries[index], [name]: finalValue };
      return { ...prev, additionEntries: entries };
    });
  };

  const addEntry = () =>
    setForm((prev) => ({
      ...prev,
      additionEntries: [...prev.additionEntries, createEntry()],
    }));
  const removeEntry = (idx) =>
    setForm((prev) => ({
      ...prev,
      additionEntries: prev.additionEntries.filter((_, i) => i !== idx),
    }));
  const resetForm = () =>
    !isEditMode &&
    setForm({
      blContainerStockDetailsId: stockDetailIdFromUrl || "",
      lotId: "",
      date: todayDate,
      labourRate: "",
      additionEntries: Array.from({ length: DEFAULT_ENTRY_COUNT }, () =>
        createEntry(),
      ),
    });

  const handleBackToCreate = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stockProcessingId");
    nextParams.delete("date");
    if (originParam === "history") {
      nextParams.set("view", "history");
    }
    setSearchParams(nextParams);

    setOriginalProcessingWeight(0);
    // Reset form to initial state
    setForm({
      blContainerStockDetailsId: stockDetailIdFromUrl || "",
      lotId: lotIdParam || form.lotId || "",
      date: todayDate,
      stockCategoryId: "",
      labourRate: "",
      additionEntries: Array.from({ length: DEFAULT_ENTRY_COUNT }, () =>
        createEntry(),
      ),
    });
    setFieldErrors({});
  };

  const handleEditHistorySummary = (item) => {
    const lotId = lotIdParam || form.lotId;
    const dateStr = item.date
      ? new Date(item.date).toISOString().split("T")[0]
      : "";

    // Update search params to trigger the loadEditData effect
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("lotId", String(lotId));
    nextParams.set("date", dateStr);
    if (item.stockProcessingId) {
      nextParams.set("stockProcessingId", String(item.stockProcessingId));
    }
    nextParams.delete("view"); // Remove view=history if it was there to enter edit mode
    setSearchParams(nextParams);

    // Smooth scroll to form
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleDeleteHistory = async (item) => {
    const stockId = item.stockProcessingId;
    const lotId = lotIdParam || form.lotId;

    if (!stockId) {
      alertService.error("Invalid processing record selected.");
      return;
    }

    const result = await alertService.deleteConfirm(
      "this specific stock processing record",
    );
    if (!result.isConfirmed) return;

    try {
      await stockProcessingService.deleteStockProcessingById(stockId);
      alertService.success("Deleted successfully.");

      // If we are deleting the record currently being edited, clear the edit state
      const currentEditId = searchParams.get("stockProcessingId");
      if (String(stockId) === String(currentEditId)) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("stockProcessingId");
        nextParams.delete("date");
        if (originParam === "history") {
          nextParams.set("view", "history");
        }
        setSearchParams(nextParams);

        setOriginalProcessingWeight(0);
        // Reset form manually since resetForm() might have isEditMode check
        setForm({
          blContainerStockDetailsId: stockDetailIdFromUrl || "",
          lotId: lotIdParam || form.lotId || "",
          date: todayDate,
          stockCategoryId: form.stockCategoryId || "",
          labourRate: "",
          additionEntries: Array.from({ length: DEFAULT_ENTRY_COUNT }, () =>
            createEntry(),
          ),
        });
      }

      // Refresh data
      loadHistoryData();
      loadLotwiseData();
      loadTransferHistoryData();
    } catch (err) {
      alertService.error(err.message || "Failed to delete.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const errors = {};
    if (
      !stockDetailIdFromUrl &&
      !isEditMode &&
      !form.blContainerStockDetailsId
    ) {
      errors.blContainerStockDetailsId = "BL Container is required";
    }
    if (!form.lotId) errors.lotId = "Primary Lot is required";
    if (!form.stockCategoryId)
      errors.stockCategoryId = "Deduction Category is required";
    if (!form.date) {
      errors.date = "Processing Date is required";
    } else if (form.stockCategoryId) {
      const selectedStock = lotwiseStock.find(
        (s) => String(s.stockCategoryId) === String(form.stockCategoryId),
      );
      if (selectedStock && selectedStock.convertedStockDate) {
        const processingDate = new Date(form.date);
        const convertedDate = new Date(selectedStock.convertedStockDate);
        processingDate.setHours(0, 0, 0, 0);
        convertedDate.setHours(0, 0, 0, 0);

        if (processingDate < convertedDate) {
          const formattedConvertedDate =
            convertedDate.toLocaleDateString("en-GB");
          errors.date = `Processing Date cannot be earlier than the stock conversion date (${formattedConvertedDate})`;
        }
      }
    }
    if (!form.labourRate) errors.labourRate = "Labour Rate is required";

    let hasEntryError = false;
    const additionCategorySet = new Set();

    form.additionEntries.forEach((en, idx) => {
      // If any field in the row is filled, the whole row needs validation
      if (en.stockCategoryId || en.weight || en.yardId) {
        if (!en.stockCategoryId) {
          errors[`entry-${idx}-stockCategoryId`] = "Category is required";
          hasEntryError = true;
        } else {
          if (additionCategorySet.has(String(en.stockCategoryId))) {
            errors[`entry-${idx}-stockCategoryId`] =
              "Duplicate category selected";
            hasEntryError = true;
          }
          additionCategorySet.add(String(en.stockCategoryId));
        }

        if (!en.weight || parseFloat(en.weight) <= 0) {
          errors[`entry-${idx}-weight`] = "Weight is required";
          hasEntryError = true;
        }
      }
    });

    const validEntries = form.additionEntries.filter(
      (en) => en.stockCategoryId && en.weight && parseFloat(en.weight) > 0,
    );

    // --- Joint Weight Validation ---
    if (form.stockCategoryId) {
      const sourceStock = lotwiseStock.find(
        (s) => String(s.stockCategoryId) === String(form.stockCategoryId),
      );
      if (sourceStock && sourceStock.isJoint) {
        const totalOutputWeight = validEntries.reduce(
          (sum, en) => sum + (parseFloat(en.weight) || 0),
          0,
        );
        // In Edit Mode, the current record's weight is already deducted from remainingWeight.
        // We add it back to calculate the true available limit for this update.
        const availableWeight =
          (sourceStock.remainingWeight || 0) +
          (isEditMode ? originalProcessingWeight : 0);

        if (totalOutputWeight > availableWeight) {
          errors.additionEntries = `Total weight (${totalOutputWeight.toLocaleString()} Kg) exceeds available joint weight (${availableWeight.toLocaleString()} Kg)`;
        }
      }
    }

    if (validEntries.length === 0 && !hasEntryError) {
      errors.additionEntries =
        "Add at least one material with category and weight.";
    }

    if (Object.keys(errors).length > 0 || hasEntryError) {
      setFieldErrors(errors);
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payload = {
      lotId: Number(form.lotId),
      stockCategoryId: Number(form.stockCategoryId),
      date: form.date,
      labourRate: Number(form.labourRate),
      additionEntries: validEntries.map((en) => ({
        stockCategoryId: Number(en.stockCategoryId),
        yardId: en.yardId ? Number(en.yardId) : undefined,
        lotId: en.lotId ? Number(en.lotId) : Number(form.lotId),
        weight: Number(en.weight),
        isJoint: Number(en.isJoint) === 1 ? 1 : 0,
      })),
    };

    setIsSubmitting(true);
    try {
      if (isEditMode)
        await stockProcessingService.updateBulkStockProcessing(payload);
      else await stockProcessingService.bulkCreateStockProcessing(payload);
      alertService.success("Saved successfully.");
      // Navigate back to the stock processing list
      navigate("/stock-processing");
    } catch (err) {
      setError(err.message);
      alertService.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRowClick = async (item) => {
    const rowId = item.stockProcessingId;
    if (!rowId) return;

    const nextExpanded = new Set(expandedRows);
    if (nextExpanded.has(rowId)) {
      nextExpanded.delete(rowId);
      setExpandedRows(nextExpanded);
    } else {
      nextExpanded.add(rowId);
      setExpandedRows(nextExpanded);

      if (!rowDetails[rowId] && !loadingDetails[rowId]) {
        setLoadingDetails((prev) => ({ ...prev, [rowId]: true }));
        try {
          const dateStr = item.date ? item.date.split("T")[0] : "";
          const detail = await stockProcessingService.getStockProcessingDetail({
            lotId: item.lotId,
            date: dateStr,
            stockProcessingId: rowId,
          });
          setRowDetails((prev) => ({ ...prev, [rowId]: detail }));
        } catch (err) {
          console.error("Failed to fetch detail for row", rowId, err);
          alertService.error(
            err.message || "Failed to load processing details.",
          );
        } finally {
          setLoadingDetails((prev) => ({ ...prev, [rowId]: false }));
        }
      }
    }
  };

  const renderExpandableContent = (item) => {
    const rowId = item.stockProcessingId;
    const isLoading = loadingDetails[rowId];
    const details = rowDetails[rowId];

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
          <span className="ml-2 text-xs font-semibold text-slate-500">
            Loading details...
          </span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="py-3 text-center text-xs font-medium text-slate-400 bg-slate-50/50 dark:bg-slate-900/50">
          No details loaded.
        </div>
      );
    }

    const additionEntries = details.additionEntries || [];

    if (additionEntries.length === 0) {
      return (
        <div className="py-3 text-center text-xs font-medium text-slate-400 bg-slate-50/50 dark:bg-slate-900/50">
          No addition entries found for this record.
        </div>
      );
    }

    return (
      <div className="p-4 bg-slate-50/80 dark:bg-slate-950/40 border-l-4 border-primary-500 rounded-r-lg m-2">
        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
          Processed Material Additions
        </h4>
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="px-3 py-2">Sr. No.</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Yard</th>
                <th className="px-3 py-2 text-right">Weight (Kg)</th>
                <th className="px-3 py-2 text-center">Joint?</th>
                <th className="px-3 py-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {additionEntries.map((entry, idx) => {
                const categoryName =
                  stockCategoryOptions.find(
                    (opt) =>
                      String(opt.value) === String(entry.stockCategoryId),
                  )?.label || `Category #${entry.stockCategoryId}`;
                const yardName =
                  yardOptions.find(
                    (opt) => String(opt.value) === String(entry.yardId),
                  )?.label || "-";
                const lotNo =
                  lotOptions.find(
                    (opt) => String(opt.value) === String(entry.lotId),
                  )?.label || "-";

                return (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                  >
                    <td className="px-3 py-2 font-mono text-slate-400">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-200">
                      {categoryName}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                      {yardName}
                    </td>

                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                      {Number(entry.weight || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {entry.isJoint ? (
                        <span className="text-[9px] font-black bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                          Joint
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                      {Number(entry.totalLabourRate || 0).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const historyColumns = [
    {
      key: "date",
      label: "Date",
      render: (item) => formatDate(item.date),
    },
    { key: "lotNo", label: "Lot No", render: (item) => item.lotNo || "-" },
    {
      key: "fromStockCategoryName",
      label: "Source Category",
      render: (item) => (
        <span className="text-xs font-bold text-slate-500">
          {item.fromStockCategoryName || "-"}
        </span>
      ),
    },

    {
      key: "processingWeight",
      label: "Processing Qty",
      headerClassName: "text-right",
      className: "text-right font-mono font-bold",
      render: (item) => Number(item.processingWeight || 0).toLocaleString(),
    },
    {
      key: "labourRate",
      label: "Rate",
      headerClassName: "text-right",
      className: "text-right font-mono",
      render: (item) => Number(item.labourRate || 0).toLocaleString(),
    },
    {
      key: "totalLabourCost",
      label: "Total Cost",
      headerClassName: "text-right",
      className: "text-right font-mono text-emerald-600 font-bold",
      render: (item) => Number(item.totalLabourCost || 0).toLocaleString(),
    },
    {
      key: "actions",
      label: "Action",
      headerClassName: "text-center",
      className: "text-center",
      render: (item) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex h-8 w-8 items-center justify-center rounded-full p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleEditHistorySummary(item);
            }}
            disabled={isLocked}
            title={
              isLocked ? "Cannot edit locked lot" : "Edit this day's processing"
            }
          >
            <i className="material-icons text-base">edit</i>
          </Button>
          {!isLocked && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteHistory(item);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-red-400 hover:bg-rose-100 hover:text-red-600 transition-all active:scale-90"
              title="Delete this day's processing"
            >
              <i className="material-icons text-base">delete_outline</i>
            </button>
          )}
        </div>
      ),
    },
  ];

  const transferHistoryColumns = [
    {
      key: "createdDate",
      label: "Date",
      render: (item) => formatDate(item.createdDate),
    },
    {
      key: "fromStockCategoryName",
      label: "From Category",
      render: (item) => (
        <span className="text-xs font-bold text-slate-500">
          {item.fromStockCategoryName || "-"}
        </span>
      ),
    },
    {
      key: "toLotNo",
      label: "To Lot",
      render: (item) => item.toLotNo || "-",
    },
    {
      key: "toStockCategoryName",
      label: "To Category",
      render: (item) => (
        <span className="text-xs font-bold text-slate-500">
          {item.toStockCategoryName || "-"}
        </span>
      ),
    },
    {
      key: "yardNo",
      label: "Yard",
      render: (item) => item.yardNo || item.yardName || "-",
    },
    {
      key: "transferWeight",
      label: "Weight (Kg)",
      headerClassName: "text-right",
      className: "text-right font-mono font-bold text-amber-600",
      render: (item) => Number(item.transferWeight || 0).toLocaleString(),
    },
  ];

  return (
    <div className="space-y-3 max-w-[1400px] mx-auto animate-in fade-in duration-500 pb-10">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
          {isEditMode ? "Edit" : ""}{" "}
          <span className="text-primary-600">Stock Processing</span>
        </h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/stock-processing")}
          >
            Back to List
          </Button>
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      {selectedStockDetail && (
        <div className="flex flex-col gap-3">
          <div className="w-full">
            <MasterDetailSummary
              title="Stock Processing Details"
              details={[
                { label: "Lot No", value: selectedStockDetail?.lotNo || "-" },
                {
                  label: "Original Weight",
                  value: `${Number(selectedStockDetail?.originalWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Remaining Original Weight",
                  value: `${Number(
                    selectedStockDetail?.remainingOriginalWeight !== undefined
                      ? Math.max(0, selectedStockDetail.remainingOriginalWeight)
                      : Math.max(0, (selectedStockDetail?.originalWeight || 0) - (selectedStockDetail?.processedWeight || 0))
                  ).toLocaleString()} Kg`,
                },
                {
                  label: "Joint Weight",
                  value: `${Number(selectedStockDetail?.totalJointWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Processed Weight",
                  value: `${Number(selectedStockDetail?.processedWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Remaining Weight",
                  value: `${Number(selectedStockDetail?.remainingWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Transfer Weight",
                  value: `${Number(selectedStockDetail?.totalTransferWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Soldout Weight",
                  value: `${Number(selectedStockDetail?.totalSalesWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Excess Weight",
                  value: `${Number(selectedStockDetail?.excessWeight || 0).toLocaleString()} Kg`,
                },
                {
                  label: "Wastage Weight",
                  value: `${Number(selectedStockDetail?.wastageWeight || 0).toLocaleString()} Kg`,
                },
              ]}
              isLoading={isLoadingDetail}
            />
          </div>

          {/* --- Available Stock Summary --- */}
          {lotwiseStock.length > 0 && (
            <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 animate-in zoom-in duration-300">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
                  <i className="material-icons text-lg">inventory_2</i>
                </div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-tight">
                  Available Stock
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {lotwiseStock.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() =>
                      handleSelectCategoryFromGrid(String(item.stockCategoryId))
                    }
                    className={`flex flex-col p-2.5 rounded-xl border transition-all cursor-pointer hover:scale-[1.01] active:scale-95 ${
                      String(form.stockCategoryId) ===
                      String(item.stockCategoryId)
                        ? "bg-primary-50 border-primary-500/50 shadow-sm shadow-primary-500/10 dark:bg-primary-900/20 dark:border-primary-500/50"
                        : "bg-slate-50 border-slate-100 hover:border-primary-500/30 dark:bg-slate-800/50 dark:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100 dark:border-slate-800">
                      <span
                        className={`text-[10px] font-black uppercase truncate ${
                          String(form.stockCategoryId) ===
                          String(item.stockCategoryId)
                            ? "text-primary-700"
                            : "text-slate-700 dark:text-slate-300"
                        }`}
                        title={item.stockCategoryName}
                      >
                        {item.stockCategoryName}
                      </span>
                      {item.isJoint && (
                        <span className="text-[8px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                          Joint
                        </span>
                      )}
                    </div>

                    <div className="relative grid grid-cols-2 gap-x-4 gap-y-1">
                      {/* Vertical Divider */}
                      <div className="absolute left-[calc(50%)] top-0 bottom-[24px] w-[1px] bg-slate-200 dark:bg-slate-700 -translate-x-1/2"></div>

                      <div className="flex items-center justify-between pr-2">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                          Total(Original) :
                        </span>
                        <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 font-mono">
                          {Number(item.weight || 0).toLocaleString()}{" "}
                          <span className="text-[8px] font-normal">Kg</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between pl-2">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                          Remaining (Original) :
                        </span>
                        <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 font-mono">
                          {Number(
                            item.remainingOriginalWeight || 0,
                          ).toLocaleString()}{" "}
                          <span className="text-[8px] font-normal">Kg</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between pr-2">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                          Transferred :
                        </span>
                        <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 font-mono">
                          {Number(
                            item.totalTransferWeight || 0,
                          ).toLocaleString()}{" "}
                          <span className="text-[8px] font-normal">Kg</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between pl-2">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                          Processed :
                        </span>
                        <span className="text-[10px] font-bold text-emerald-600 font-mono">
                          {Number(item.processedWeight || 0).toLocaleString()}{" "}
                          <span className="text-[8px] font-normal">Kg</span>
                        </span>
                      </div>
                      {!item.isJoint && (
                        <>
                          <div className="flex items-center justify-between pr-2">
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                              Excess :
                            </span>
                            <span className="text-[10px] font-bold text-amber-600 font-mono">
                              {Number(item.excessWeight || 0).toLocaleString()}{" "}
                              <span className="text-[8px] font-normal">Kg</span>
                            </span>
                          </div>
                          <div className="flex items-center justify-between pl-2">
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                              Wastage :
                            </span>
                            <span className="text-[10px] font-bold text-rose-500 font-mono">
                              {Number(item.wastageWeight || 0).toLocaleString()}{" "}
                              <span className="text-[8px] font-normal">Kg</span>
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex items-center  gap-2 col-span-2 mt-1.5 pt-1.5 border-t border-slate-100/50 dark:border-slate-800/50">
                        <span className="text-[9px] font-black text-primary-500 uppercase tracking-tight">
                          Remaining:
                        </span>
                        <span className="text-xs font-black text-primary-600 font-mono">
                          {Number(item.remainingWeight || 0).toLocaleString()}{" "}
                          <span className="text-[9px] font-normal text-slate-400">
                            Kg
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- HISTORY SECTION --- */}
      {(lotIdParam || form.lotId) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 font-bold text-sm">
              <i className="material-icons text-base">history</i>
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
              Processing History
            </h2>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <CrudTable
              columns={historyColumns}
              rows={historyItems}
              isLoading={isLoadingHistory}
              showSearch={false}
              showPagination={false}
              showActions={false}
              tableScrollHeight="250px"
              expandable={true}
              expandedRows={expandedRows}
              renderExpandableContent={renderExpandableContent}
              onRowClick={handleRowClick}
              rowKeyField="stockProcessingId"
            />
          </div>
        </div>
      )}

      {/* --- STOCK TRANSFER HISTORY SECTION --- */}
      {(lotIdParam || form.lotId) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 font-bold text-sm">
              <i className="material-icons text-base">swap_horiz</i>
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
              Stock Transfer History
            </h2>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <CrudTable
              columns={transferHistoryColumns}
              rows={transferHistoryItems}
              isLoading={isLoadingTransferHistory}
              showSearch={false}
              showPagination={false}
              showActions={false}
              tableScrollHeight="250px"
              rowKeyField="transferMasterId"
            />
          </div>
        </div>
      )}

      {/* --- FORM SECTION --- */}
      {viewParam !== "history" &&
        (originParam !== "history" || isEditMode) &&
        (!isLocked ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 animate-in slide-in-from-bottom duration-500">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 font-bold text-sm">
                  01
                </div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                  Processing Details
                </h2>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-2">
                  {isEditMode && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBackToCreate}
                      className="border-rose-200 text-rose-600 hover:bg-rose-50"
                    >
                      <i className="material-icons text-base mr-1">close</i>{" "}
                      Cancel Edit
                    </Button>
                  )}
                  {!isEditMode && (
                    <Button type="button" size="sm" onClick={addEntry}>
                      <i className="material-icons text-base mr-1">add</i> Add
                      Material
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl animate-in fade-in slide-in-from-top-1 duration-500">
                  <i className="material-icons text-amber-600 text-sm">info</i>
                  <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-tight leading-tight">
                    Note: Once joint data is added, it cannot be updated. To
                    change it, you must delete and re-add the record.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-4 border-b border-slate-100 dark:border-slate-800 pb-5">
                <SearchableDropdown
                  label="Primary Lot"
                  value={String(form.lotId || "")}
                  options={lotOptions}
                  placeholder="Select Lot"
                  onChange={(e) => updateField("lotId", e.target.value)}
                  disabled={true}
                  error={fieldErrors.lotId}
                />
                <SearchableDropdown
                  label="Processing Category"
                  value={String(form.stockCategoryId || "")}
                  options={lotwiseStock.map((s) => ({
                    label: s.stockCategoryName,
                    value: String(s.stockCategoryId),
                  }))}
                  placeholder="Select Source"
                  onChange={(e) =>
                    updateField("stockCategoryId", e.target.value)
                  }
                  disabled={isProcessingJointDisabled}
                  error={fieldErrors.stockCategoryId}
                  hint={
                    form.stockCategoryId &&
                    (() => {
                      const stock = lotwiseStock.find(
                        (s) =>
                          String(s.stockCategoryId) ===
                          String(form.stockCategoryId),
                      );
                      return stock
                        ? `Available: ${Number(stock.remainingWeight || 0).toLocaleString()} Kg`
                        : null;
                    })()
                  }
                />
                <DatePicker
                  label="Processing Date"
                  value={form.date}
                  minDate={minProcessingDate}
                  onChange={(date) => updateField("date", date)}
                  disabled={isProcessingJointDisabled}
                  error={fieldErrors.date}
                />
                <Input
                  label="Labour Rate"
                  type="text"
                  value={form.labourRate}
                  onChange={(e) => updateField("labourRate", e.target.value)}
                  placeholder="e.g. 6.5"
                  className="font-bold"
                  // disabled={isProcessingJointDisabled}
                  error={fieldErrors.labourRate}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                    Material Additions
                  </h3>
                  {fieldErrors.additionEntries && (
                    <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight">
                      {fieldErrors.additionEntries}
                    </p>
                  )}
                </div>
                <div className="grid gap-4">
                  {form.additionEntries.map((entry, index) => {
                    const isJointRowLocked =
                      isEditMode && entry.isOriginalJoint;

                    return (
                      <div
                        key={`entry-${index}`}
                        className={`group relative grid grid-cols-1 gap-3 rounded-2xl border p-3 transition-all md:grid-cols-12 md:items-end md:gap-4 md:p-4 ${
                          isJointRowLocked
                            ? "bg-slate-100/50 border-slate-200 opacity-80 cursor-not-allowed dark:bg-slate-900/30 dark:border-slate-800"
                            : "bg-slate-50/50 border-slate-100 hover:border-primary-500/30 hover:bg-white dark:border-slate-800 dark:bg-slate-800/50 dark:hover:bg-slate-800"
                        }`}
                      >
                        <div className="md:col-span-3">
                          <SearchableDropdown
                            label="Category"
                            value={String(entry.stockCategoryId || "")}
                            options={stockCategoryOptions.filter(
                              (opt) =>
                                String(opt.value) !==
                                  String(form.stockCategoryId) &&
                                !form.additionEntries.some(
                                  (e, i) =>
                                    i !== index &&
                                    String(e.stockCategoryId) ===
                                      String(opt.value),
                                ),
                            )}
                            placeholder="Select Category"
                            onChange={(e) =>
                              updateEntry(
                                index,
                                "stockCategoryId",
                                e.target.value,
                              )
                            }
                            onAdd={() => openModal("stockCategory")}
                            disabled={isJointRowLocked}
                            error={
                              fieldErrors[`entry-${index}-stockCategoryId`]
                            }
                          />
                        </div>

                        <div className="md:col-span-2">
                          <Input
                            label="Weight (Kg)"
                            type="number"
                            value={entry.weight}
                            onChange={(e) =>
                              updateEntry(index, "weight", e.target.value)
                            }
                            disabled={isJointRowLocked}
                            placeholder="0.00"
                            className="font-bold"
                            error={fieldErrors[`entry-${index}-weight`]}
                          />
                        </div>
                        <div className="flex items-end justify-between gap-4 md:col-span-2 md:justify-end">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              Joint
                            </span>
                            <div className="flex h-9 items-center">
                              <input
                                type="checkbox"
                                className={`h-5 w-5 rounded-lg border-slate-300 text-primary-600 focus:ring-primary-500 transition-all ${isJointRowLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                checked={Number(entry.isJoint) === 1}
                                disabled={isJointRowLocked}
                                onChange={(e) =>
                                  updateEntry(
                                    index,
                                    "isJoint",
                                    e.target.checked ? 1 : 0,
                                  )
                                }
                              />
                            </div>
                          </div>
                          {!isEditMode && (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-transparent select-none">
                                Delete
                              </span>
                              <button
                                type="button"
                                onClick={() => removeEntry(index)}
                                className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-red-400 hover:bg-rose-100 hover:text-red-600 dark:text-slate-600 dark:hover:bg-rose-900/20 transition-all active:scale-90"
                              >
                                <i className="material-icons text-xl">
                                  delete_outline
                                </i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-100 dark:border-slate-800">
                {!isEditMode && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    onClick={resetForm}
                  >
                    Reset Form
                  </Button>
                )}
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  className="px-8 h-10 text-xs font-bold uppercase tracking-widest shadow-lg shadow-primary-500/20"
                >
                  {isSubmitting
                    ? "Saving..."
                    : isEditMode
                      ? "Update Processing"
                      : "Save Processing"}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-10 text-center dark:border-amber-900/30 dark:bg-amber-900/10 animate-in zoom-in duration-500">
            <div className="flex flex-col items-center justify-center gap-3 grayscale opacity-60">
              <i className="material-icons text-5xl text-amber-600">
                lock_person
              </i>
              <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">
                Stock Lot is Closed
              </h3>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest max-w-md mx-auto leading-relaxed">
                This lot has been finalized and locked for processing. New
                entries or modifications are no longer permitted for this
                record.
              </p>
            </div>
          </div>
        ))}
      {renderModals()}
    </div>
  );
}

export default BulkStockProcessingCreatePage;
